import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { UsersPageWrapper } from 'src/components/profileComponents/Users/components/UsersPageWrapper';
import { ICrumbs } from 'src/components/profileComponents/Breadcrumbs';
import { translations } from 'src/constants/translations';
import { pathList } from 'src/containers/Profile/routeList';
import {
  Controller, SubmitHandler, useFieldArray, useForm,
} from 'react-hook-form';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import { appIcon } from 'src/assets';
import { ButtonApp } from 'src/components/ApartmentPageV2/Button';
import { useUserCreate } from 'src/components/profileComponents/Users/hooks/useUserCreate';
import { EMAIL_REGEX, NAME_REGEX, RolesMap } from 'src/components/profileComponents/Users/constants';
import {
  IDeveloperWithSharedComplexes,
  ISaveUserData, IUserCreateComplexes, IUserCreateRole, UserRow,
} from 'src/typings/usersManage';
import { TypeOfRole } from 'src/typings/user';
import clsx from 'clsx';
import { useToastr } from 'src/hooks/useToastr';
import { Project } from 'src/components/profileComponents/Users/components/CreateUser/conponents/Project';
import { RandomInteger } from 'src/helpers/randomInteger';
import { UserInput } from 'src/components/profileComponents/Users/components/UserInput';
import { UserAutocomplete } from 'src/components/profileComponents/Users/components/UserAutocomplete';
import { useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { Option } from 'src/typings/app';
import { IComplex } from 'src/typings/complexes';
import { Developer } from 'src/components/profileComponents/Users/components/CreateUser/conponents/Developer';
import { QUERY_KEYS } from 'src/constants/queryKeys';
import { useUsersManage } from 'src/components/profileComponents/Users/hooks/useUsersManage';
import { useStyles as useCommonUsers } from 'src/components/profileComponents/Common';
import { useStyles } from './styles';

type FormValues = {
  users: UserRow[];
};

const getEmptyRow = () => ({
  id: RandomInteger(), username: '', email: '', role: TypeOfRole.manager,
});

const defaultValues: FormValues = {
  users: [
    getEmptyRow(),
  ],
};

export const CreateUser: React.FC = () => {
  const { userId }: { userId: string | undefined } = useParams();
  const isEditMode = !!userId;
  const {
    initialData, developersIds, setDevelopersIds, saveUserData,
  } = useUserCreate(userId);
  const { queryClient } = useUsersManage();
  const [search, setSearch] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [roleOptions, setRoleOptions] = useState<{ label: string, value: TypeOfRole}[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<IUserCreateComplexes[]>([]);
  const commonUsersStyles = useCommonUsers();
  const styles = useStyles();
  const { toastrClear, toastrSuccess, toastrError } = useToastr();
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setError,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues, mode: 'onChange' });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'users',
    keyName: 'customId',
  });

  const defaultDeveloper: Option<string>[] = useMemo(() => {
    const tmpDev: Option<string>[] | undefined = initialData?.authenticatedUser.developersWithSharedComplexes
      ?.filter((developer) => (!!developersIds?.length ? developersIds.includes(developer['@id']) : true))
      ?.map((developer) => ({ label: String(developer.name), value: String(developer['@id']) }));
    return tmpDev || [];
  }, [initialData, developersIds]);

  useEffect(() => {
    if (isEditMode && initialData?.editableUser?.username && initialData?.editableUser.email) {
      let complexes: IUserCreateComplexes[] = [];
      initialData.editableUser.developersWithSharedComplexes.forEach((dev) => {
        complexes = [...complexes, ...dev.complexes];
      });
      reset({
        users: [{
          id: initialData.editableUser.id, username: initialData.editableUser.username, email: initialData.editableUser.email, role: initialData.editableUser.role?.name,
        }],
      });
      setSelectedProjects(complexes);
    }
  }, [initialData, isEditMode, reset]);

  useEffect(() => {
    if (!roleOptions?.length) {
      setRoleOptions(initialData?.authenticatedUser.assignableRoles?.map((role) => getRoleOption(role)) || []);
    }
  }, [initialData]);

  useEffect(() => {
    if (!initialData) return;
    setDevelopersIds((prev) => {
      const tmpDevIds = defaultDeveloper.map((item) => item.value);
      const value = [tmpDevIds[0]];
      return (prev?.join() === tmpDevIds.join() ? prev : value);
    });
  }, [initialData, defaultDeveloper, isEditMode]);

  const isEntityIncludesEntity = <T extends Record<string, any>, K extends keyof T = '@id'>(
    arr: T[],
    searchElement: T,
    key?: K,
  ): boolean => {
    const compareKey = (key ?? '@id') as K;
    return arr.map((item) => item[compareKey]).includes(searchElement[compareKey]);
  };

  const developersOptions = useMemo(() => initialData?.authenticatedUser.developersWithSharedComplexes?.map((developer) => ({ label: developer.name, value: developer['@id'] })) || [], [initialData]);
  const searchFilter = (complex: IUserCreateComplexes | IComplex) => !search || (search.length && String(complex.title).toLowerCase().includes(search.toLowerCase()));

  const selectedDevelopers = useMemo<IDeveloperWithSharedComplexes[]>(() => {
    const list = initialData?.authenticatedUser.developersWithSharedComplexes;
    return list?.filter((dev) => {
      return dev.complexes.some((complex) => isEntityIncludesEntity(selectedProjects, complex));
    }) || [];
  }, [initialData, selectedProjects]);

  const filteredSelectedProjectsByDeveloper = useMemo(() => {
    return selectedDevelopers.find((dev) => developersIds.includes(dev['@id']))
      ?.complexes.filter((project) => isEntityIncludesEntity(selectedProjects, project)) || [];
  }, [selectedProjects, developersIds, selectedDevelopers]);

  const projectsList = useMemo(() => {
    return initialData?.authenticatedUser.developersWithSharedComplexes
      .find((dev) => developersIds.includes(dev['@id']))
      ?.complexes?.filter(searchFilter);
  }, [initialData, developersIds, search]);

  const getSelectedProjectsCount = useCallback((dev: IDeveloperWithSharedComplexes): number => {
    return dev.complexes.filter((c) => isEntityIncludesEntity(selectedProjects, c)).length || 0;
  }, [selectedDevelopers, selectedDevelopers]);

  const getRoleOption = (role: IUserCreateRole) => {
    // @ts-ignore: nextLine
    return { label: RolesMap[role.name], value: role.name };
  };

  const isDisabled = !selectedProjects.length || Object.keys(errors).length > 0;

  const normalizeEmail = (s: string) => (s ?? '').trim().toLowerCase();
  const isUniqueEmail = useCallback(
    (value: string, currentIndex: number) => {
      const val = normalizeEmail(value);
      if (!val) return true;
      const users = getValues('users') ?? [];
      const hasDup = users.some(
        (u, i) => i !== currentIndex && normalizeEmail(u?.email ?? '') === val,
      );
      return !hasDup;
    }, [getValues],
  );

  const toggleProject = useCallback((project: IUserCreateComplexes) => {
    const tmpProjects = [...selectedProjects];
    const found = tmpProjects.find((pro) => pro['@id'] === project['@id']);
    if (found) {
      const index = tmpProjects.findIndex((item) => item['@id'] === found['@id']);
      tmpProjects.splice(index, 1);
    } else {
      tmpProjects.push(project);
    }
    setSelectedProjects(tmpProjects);
    trigger();
  }, [projectsList, selectedProjects, trigger]);

  const selectAllProjects = useCallback(() => {
    if (projectsList?.length) {
      setSelectedProjects(Array.from(new Set([...selectedProjects, ...projectsList])));
      trigger();
    }
  }, [selectedProjects, projectsList, trigger]);

  const isProjectsListChanged = useCallback((developer: IDeveloperWithSharedComplexes) => {
    return !!selectedDevelopers
      .filter((dev) => dev['@id'] === developer['@id'])
      .find((dev) => {
        const foundComplexes = dev.complexes.filter((complex) => isEntityIncludesEntity(selectedProjects, complex));
        return dev.complexes.length >= foundComplexes.length;
      });
  }, [selectedDevelopers, selectedProjects]);

  const isDeveloperSelected = useCallback((devId: string) => developersIds.includes(devId), [developersIds]);
  const isChecked = useCallback((project: IUserCreateComplexes) => isEntityIncludesEntity(selectedProjects, project), [selectedProjects]);

  const onDeveloperChange = (e: Option<string>) => {
    setDevelopersIds([e.value]);
  };

  const addRow = useCallback(() => {
    append({
      id: RandomInteger(), username: '', email: '', role: TypeOfRole.manager,
    });
    setTimeout(() => {
      trigger();
    });
  }, [append, trigger, TypeOfRole]);

  const submitBtnText: string = useMemo(() => (isEditMode ? 'Save changes' : 'Create and assign user(s)'), [isEditMode]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!Object.keys(errors).length && selectedProjects.length && initialData) {
      setIsLoading(true);
      const saveData: ISaveUserData = {
        users: data.users.map((user) => {
          const foundRole = initialData.authenticatedUser.assignableRoles.filter((role) => role.name === user.role);
          return {
            id: user.id, username: user.username, email: user.email, role: foundRole[0]['@id'],
          };
        }),
        complexes: selectedProjects.map((project) => project['@id']),
        developers: selectedDevelopers.map((developer) => developer['@id']),
      };
      saveUserData(saveData, isEditMode ? 'put' : 'post')
        .then((res) => {
          switch (res.status) {
            case 422: {
              toastrClear();
              toastrError(res.data.title);
              res.data.violations.map((vio) => setError(vio.propertyPath,
                {
                  type: 'server',
                  message: vio.message,
                }));
              break;
            }
            case 200: {
              queryClient.invalidateQueries([QUERY_KEYS.MANAGE_USERS_INITIAL]);
              toastrClear();
              toastrSuccess(isEditMode ? 'User updated' : 'User(s) Created');
              if (!isEditMode) {
                reset({
                  users: [
                    getEmptyRow(),
                  ],
                });
                setSelectedProjects([]);
              }
              break;
            }
            default: return null;
          }
          setIsLoading(false);
          return null;
        });
    }
  };

  const crumbs: ICrumbs[] = [
    {
      name: translations.usersLink.toUpperCase(),
      url: isEditMode ? `/profile/${pathList.users}/manage` : `/profile/${pathList.users}`,
    },
    {
      name: isEditMode ? translations.editUser.toUpperCase() : translations.createNewUsers.toUpperCase(),
    },
  ];

  return (
    <UsersPageWrapper
      crumbs={crumbs}
      title={isEditMode ? 'EDIT USER' : 'CREATE NEW USER(S)'}
    >
      <div className={styles.container}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.stepOne}>
            <div className={styles.stepOneWrapper}>
              <div className={styles.stepHeader}>{`${isEditMode ? 'STEP 1 - USER INFO' : 'STEP 1 - ADD USER(S)'}`}</div>
              <div className={styles.stepContent}>
                <div className={styles.tableHeader}>
                  <div className="gridRow gridHeader" aria-hidden>
                    <div className="col"><span className="label">Name</span></div>
                    <div className="col"><span className="label">Email</span></div>
                    <div className="col"><span className="label">Role</span></div>
                    <div className="col" />
                  </div>
                  <div className={styles.rows}>
                    {fields.map((row, index) => (
                      <div
                        key={row.id}
                        className="gridRow"
                        role="group"
                        aria-label="user row"
                      >
                        <div className={`${styles.col} ${styles.input}`}>
                          <Controller
                            name={`users.${index}.id`}
                            defaultValue={fields[index].id}
                            control={control}
                            render={(field) => (
                              <input {...field} name={`users.${index}.id`} type="text" value={row.id} hidden readOnly />
                            )}
                          />
                          <Controller
                            name={`users.${index}.username`}
                            defaultValue={fields[index].username}
                            control={control}
                            rules={{
                              required: 'Name is required',
                              validate: {
                                charset: (v) => (v === '' || NAME_REGEX.test(v))
                                  || 'Only letters, spaces, apostrophes and hyphens',
                                min: (v) => (v?.trim()?.length ?? 0) >= 2 || 'Min 2 characters',
                                max: (v) => (v?.trim()?.length ?? 0) <= 64 || 'Max 64 characters',
                              },
                            }}
                            render={(field) => (
                              <>
                                <UserInput
                                  {...field}
                                  placeholder="Name"
                                  name={field.name}
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value)}
                                  onBlur={(e) => field.onChange(e.target.value.replace(/\s+/g, ' ').trim())}
                                />
                                {errors.users?.[index]?.username && (
                                <div className={styles.errorText}>{errors.users[index]?.username?.message}</div>
                                )}
                              </>
                            )}
                          />
                        </div>
                        <div className={`${styles.col} ${styles.input}`}>
                          <Controller
                            name={`users.${index}.email`}
                            defaultValue={fields[index].email}
                            control={control}
                            rules={{
                              required: 'Email is required',
                              validate: {
                                format: (v) => EMAIL_REGEX.test(v) || 'Invalid email format',
                                unique: (v) => isUniqueEmail(v, index) || 'Duplicate email in the list',
                              },
                            }}
                            render={(field) => (
                              <>
                                <UserInput
                                  {...field}
                                  placeholder="name@company.com"
                                  name={field.name}
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value.trim().toLowerCase())}
                                  onBlur={async () => {
                                    field.onBlur();
                                    const names = (getValues('users') ?? []).map((_, i) => `users[${i}].email`);
                                    await trigger(names);
                                  }}
                                />
                                {errors.users?.[index]?.email && (
                                <div className={styles.errorText}>
                                  {errors.users[index]?.email?.message}
                                </div>
                                )}
                              </>
                            )}
                          />
                        </div>
                        <Box className={`${styles.col} ${styles.input} role`}>
                          { roleOptions?.length > 0 && (
                          <Controller
                            name={`users.${index}.role`}
                            defaultValue={fields[index].role}
                            control={control}
                            rules={{ required: 'Role is required' }}
                            render={(field) => (
                              <>
                                <UserAutocomplete
                                  {...field}
                                  name={field.name}
                                  value={roleOptions?.find((opt) => opt.value === field.value) ?? null}
                                  options={roleOptions}
                                  onChange={(opt) => field.onChange(opt?.value ?? '')}
                                  onBlur={field.onBlur}
                                />
                                { errors.users?.[index]?.role && (
                                <div className={styles.errorText}>{ errors.users[index]?.role?.message }</div>
                                ) }
                              </>
                            )}
                          />
                          ) }
                        </Box>
                        {fields.length > 1 && (
                        <div className="col actions">
                          <button
                            type="button"
                            className="iconBtn"
                            aria-label="Delete row"
                            title="Delete row"
                            onClick={() => remove(index)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
                              <path
                                d="M4.33398 7.58333H21.6673M10.834 11.9167V18.4167M15.1673 11.9167V18.4167M5.41732
                               7.58333L6.50065 20.5833C6.50065 21.158 6.72892 21.7091 7.13525 22.1154C7.54158 22.5217
                                8.09268 22.75 8.66732 22.75H17.334C17.9086 22.75 18.4597 22.5217 18.866 22.1154C19.2724
                                 21.7091 19.5007 21.158 19.5007 20.5833L20.584 7.58333M9.75065 7.58333V4.33333C9.75065
                                  4.04602 9.86479 3.77047 10.068 3.5673C10.2711 3.36414 10.5467 3.25 10.834 3.25H15.1673C15.4546
                                   3.25 15.7302 3.36414 15.9334 3.5673C16.1365 3.77047 16.2507 4.04602 16.2507 4.33333V7.58333"
                                stroke="black"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {!isEditMode && (
                    <div className={commonUsersStyles.secondaryBtnWrap}>
                      <button
                        type="button"
                        className="secondary-btn"
                        onClick={addRow}
                      >
                        + Add row for another user
                      </button>
                    </div>
                  ) }
                </div>
              </div>
            </div>
          </div>
          <div className={styles.stepTwo}>
            <Box display="flex" flexDirection="column" gap="8px">
              <div className={styles.stepHeader}>STEP 2 - ASSIGN PROJECTS</div>
              {isEditMode && initialData?.editableUser ? (
                <div className={styles.stepHeaderDescription}>
                  Currently assigned developers and project counts:
                  { ' ' }
                  <span className="for-user">{`for ${initialData?.editableUser.username} (${initialData.editableUser.email})` }</span>
                </div>
              ) : (
                <div className={styles.stepHeaderDescription}>Currently assigned developers and project counts:</div>
              ) }
            </Box>
            <Box className={styles.developers}>
              {selectedDevelopers.map((developer) => (
                <React.Fragment key={developer.id}>
                  <Developer
                    isSelected={isDeveloperSelected}
                    isChanged={isProjectsListChanged}
                    developer={developer}
                    onClick={onDeveloperChange}
                    selectedProjectsCount={getSelectedProjectsCount(developer)}
                  />
                </React.Fragment>
              ))}
            </Box>
            <div className={styles.stepTwoContent}>
              <div className="contentActions">
                <div className="actions">
                  <div className={`${styles.input} developer`}>
                    {defaultDeveloper.length > 0 && (
                      <UserAutocomplete
                        label={translations.developer}
                        name="developer"
                        value={defaultDeveloper[0]}
                        options={developersOptions}
                        onChange={onDeveloperChange}
                      />
                    ) }
                  </div>
                  <div className={`${styles.input} search`}>
                    <UserInput
                      startAdornment={(
                        <InputAdornment position="start">
                          <img src={appIcon.iconSearch.default} alt="icon search" />
                        </InputAdornment>
                  )}
                      label="Project search"
                      placeholder="start typing the name..."
                      name="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="buttons">
                  <ButtonApp
                    onClick={selectAllProjects}
                  >
                    Select all projects
                  </ButtonApp>
                  <div className={`selected-count ${clsx(selectedProjects.length ? 'selected' : 'not-selected')}`}>
                    {`${filteredSelectedProjectsByDeveloper.length} selected`}
                  </div>
                </div>
              </div>
              <div className="contentProjectsWrapper">
                <div className="wrapper">
                  <div className="header">Selected projects:</div>
                  {filteredSelectedProjectsByDeveloper.length > 0 ? (
                    <div className="contentProjects">
                      {filteredSelectedProjectsByDeveloper.map((project) => (
                        <React.Fragment key={project.id}>
                          <Project
                            project={project}
                            isChecked={() => true}
                            toggleProject={toggleProject}
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  ) : (
                    <div className="not-selected">No projects selected</div>
                  )}
                </div>
                <div className="wrapper">
                  {projectsList && (
                    <>
                      <div className="header">All projects:</div>
                      <div className="contentProjects">
                        {projectsList.map((project) => (
                          <React.Fragment key={project.id}>
                            {!isEntityIncludesEntity(selectedProjects, project) && (
                              <Project project={project} isChecked={isChecked} toggleProject={toggleProject} />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.buttonsWrapper}>
            <div className="divider" />
            <div className="footer">
              <div className="text">The selected projects will be made available to all users created in Step 1.</div>
              <div className={`btn ${clsx(isDisabled && 'disabled')}`}>
                <ButtonApp
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isDisabled || isLoading}
                >
                  { !isLoading ? (submitBtnText) : (<CircularProgress size="20px" sx={{ color: '#fff' }} />) }
                </ButtonApp>
              </div>
            </div>
          </div>
        </form>
      </div>
    </UsersPageWrapper>
  );
};
